import { Link, getRouteApi, useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  REGION_MIN_POINTS,
  encodeFog,
  hideFog,
  parseFog,
  polygonShape,
  revealFog,
  revealsAny,
  CAMPAIGN_LIMITS,
  CONDITION_DURATIONS,
  DiceError,
  DiceExpression,
  NameIndex,
  after,
  before,
  conditionLabel,
  decodeConditions,
  describeRoll,
  expandInlineRolls,
  ignoreKeyForCandidates,
  ignoreKeyForName,
  isDown,
  rollTable,
  scanMentions,
  secureRandom,
  turnOrder,
  unknownNames,
  ENTITY_TYPE_NAMES,
  type Candidate,
  type CampaignSession,
  type Combatant,
  type CombatantDetails,
  type Condition,
  type EntrySummary,
  type IgnoredMention,
  type RunCommand,
  type RunResult,
  type SessionChanges,
} from '@realmery/realm-core';
import { RealmClientError, realms } from '../db/client.ts';
import type { AtlasMapView, CampaignView, RunData } from '../db/protocol.ts';
import { usePlayerView } from '../canvas/PlayerViewHost.tsx';
import { recordRoll, useDice } from '../dice/diceStore.ts';
import { SaveStatusLine, useAutosaver } from '../editor/useEntrySaver.tsx';
import { settings } from '../settings.ts';
import { EncounterForm } from './GameMasterPage.tsx';

/**
 * The Game Master Toolkit's Run screen (docs/PARITY-STEP-5-PLAN.md section 8), as the desktop's
 * `CampaignRunPane`: the party on the left (their sheets' hit points, resources and
 * conditions, with − and + that change the sheet itself), the encounter being run in the
 * middle (initiative, turns and rounds, hits and heals by a number or a roll, conditions,
 * Quick Add Creatures), and the dock on the right (the current session's notes and play
 * log, dice, random tables, and Scan Notes). Everything that happens goes into the play log.
 * Three columns on a wide screen; tabs on a narrow one (decision 8).
 */

const realmRoute = getRouteApi('/_app/realm/$realmKey');

const message = (e: unknown, fallback: string) => (e instanceof RealmClientError || e instanceof DiceError ? e.message : fallback);
/** The time here, as the play log writes it: "20:41". */
const stamp = () => new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });

type Pane = 'party' | 'encounter' | 'dock';
const PANE_NAMES: Record<Pane, string> = { party: 'Party', encounter: 'Encounter', dock: 'Dock' };

interface Table {
  readonly file: string;
  readonly realmKey: string;
  readonly campaignId: string;
  readonly run: RunData;
  readonly view: CampaignView;
  /** Does `work`, adds its rolls to the Dice Roller's history, and reloads; false when it failed. */
  readonly act: (work: () => Promise<RunResult | unknown>, fallback: string) => Promise<boolean>;
  readonly log: (line: string) => Promise<boolean>;
}

export function RunScreen({ view, file, realmKey }: { view: CampaignView; file: string; realmKey: string }) {
  const router = useRouter();
  const [pane, setPane] = useState<Pane>('encounter');
  const [error, setError] = useState('');
  /** The dock's typing is written before anything else happens, so a step never undoes it. */
  const flushDock = useRef<() => Promise<void>>(async () => undefined);
  const run = view.run!;
  const campaignId = view.campaign.id;

  async function act(work: () => Promise<RunResult | unknown>, fallback: string): Promise<boolean> {
    setError('');
    try {
      await flushDock.current();
      const result = await work();
      if (result && typeof result === 'object' && 'rolls' in result) for (const r of (result as RunResult).rolls) recordRoll(r.label, r.result);
      await router.invalidate();
      return true;
    } catch (e) {
      setError(message(e, fallback));
      return false;
    }
  }
  const table: Table = { file, realmKey, campaignId, run, view, act, log: (line) => act(() => realms.appendPlayLog(file, campaignId, line, stamp()), 'The play log could not be written.') };

  return (
    <div className="run-screen">
      <div className="run-tabs" role="tablist" aria-label="Run">
        {(Object.keys(PANE_NAMES) as Pane[]).map((p) => (
          <button key={p} type="button" role="tab" aria-selected={p === pane} className={p === pane ? 'tab selected' : 'tab'} onClick={() => setPane(p)}>
            {PANE_NAMES[p]}
          </button>
        ))}
      </div>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      <div className={`run-columns showing-${pane}`}>
        <section className="run-party" aria-label="Party">
          <Party table={table} />
        </section>
        <section className="run-encounter" aria-label="Encounter">
          <EncounterPane table={table} />
        </section>
        <section className="run-dock" aria-label="Dock">
          <Dock table={table} register={(flush) => (flushDock.current = flush)} />
        </section>
      </div>
    </div>
  );
}

// ---- the party (PartyPanel) ----

function Party({ table }: { table: Table }) {
  const { run, realmKey, file, campaignId, act } = table;
  return (
    <>
      <h2>Party</h2>
      {run.party.length === 0 && <p className="meta">No player characters yet. Add them on Prep's Overview.</p>}
      {run.party.map(({ entry, sheet }) => (
        <article key={entry.id} className="party-card" aria-label={entry.title}>
          <header>
            <Link to="/realm/$realmKey/entry/$entryId" params={{ realmKey, entryId: entry.id }} search={{}}>
              <strong>{entry.title}</strong>
            </Link>
            {sheet && (
              <Link to="/realm/$realmKey/sheets/$sheetId" params={{ realmKey, sheetId: sheet.id }} title="Open the sheet">
                📜
              </Link>
            )}
          </header>
          {!sheet ? (
            <p className="meta">
              No character sheet.{' '}
              <Link to="/realm/$realmKey/sheets" params={{ realmKey }}>
                Make one…
              </Link>
            </p>
          ) : (
            <>
              <p className="meta">
                {sheet.templateName}
                {sheet.defense ? ` · ${sheet.defense}` : ''}
              </p>
              {sheet.resources.map((r) => (
                <div key={r.key} className="resource-row">
                  <span className="resource-label">{r.label}</span>
                  <progress max={r.max} value={Math.max(0, Math.min(r.value, r.max))} className={r.hitPoints && r.value * 4 <= r.max ? 'low' : undefined} aria-label={`${r.label} ${r.value} of ${r.max}`} />
                  <span className="meta">
                    {r.value}/{r.max}
                  </span>
                  <button
                    type="button"
                    className="quiet"
                    aria-label={`One less ${r.label}`}
                    title={`One less ${r.label}: changes the sheet`}
                    disabled={r.value <= 0}
                    onClick={() => void act(() => realms.stepSheetResource(file, campaignId, sheet.id, r.key, -1, stamp()), 'The sheet could not be changed.')}
                  >
                    −
                  </button>
                  <button
                    type="button"
                    className="quiet"
                    aria-label={`One more ${r.label}`}
                    title={`One more ${r.label}: changes the sheet`}
                    disabled={r.value >= r.max}
                    onClick={() => void act(() => realms.stepSheetResource(file, campaignId, sheet.id, r.key, 1, stamp()), 'The sheet could not be changed.')}
                  >
                    +
                  </button>
                </div>
              ))}
              <Conditions
                conditions={sheet.conditions}
                presets={sheet.presets}
                durations={false}
                who={sheet.name}
                onRemove={(k) => void act(() => realms.setSheetConditions(file, campaignId, sheet.id, sheet.conditions.filter((x) => x !== k), `${sheet.name} is no longer ${k.name}.`, stamp()), 'The sheet could not be changed.')}
                onAdd={(name) =>
                  void act(
                    () => realms.setSheetConditions(file, campaignId, sheet.id, [...sheet.conditions.filter((x) => x.name.toLowerCase() !== name.toLowerCase()), { name, roundsLeft: 0 }], `${sheet.name} is ${name}.`, stamp()),
                    'The sheet could not be changed.',
                  )
                }
              />
            </>
          )}
        </article>
      ))}
      <h2>Cast</h2>
      {run.cast.length === 0 && <p className="meta">No cast yet.</p>}
      <ul className="cast-list">
        {run.cast.map(({ entry, sheetId, summary }) => (
          <li key={entry.id}>
            <Link to="/realm/$realmKey/entry/$entryId" params={{ realmKey, entryId: entry.id }} search={{}}>
              {entry.title}
            </Link>{' '}
            <span className="meta">{entry.typeName}</span>{' '}
            {sheetId && (
              <Link to="/realm/$realmKey/sheets/$sheetId" params={{ realmKey, sheetId }} title="Open the sheet">
                📜
              </Link>
            )}
            {summary && <div className="meta">{summary}</div>}
          </li>
        ))}
      </ul>
    </>
  );
}

/** Conditions as chips with ✕, and a way to add one: from the presets (for some rounds, or until removed), or another. */
function Conditions({
  conditions,
  presets,
  durations,
  who,
  onRemove,
  onAdd,
}: {
  conditions: readonly Condition[];
  presets: readonly string[];
  durations: boolean;
  who: string;
  onRemove: (c: Condition) => void;
  onAdd: (name: string, rounds: number) => void;
}) {
  const [name, setName] = useState('');
  const [other, setOther] = useState('');
  const [rounds, setRounds] = useState(0);
  return (
    <div className="conditions">
      {conditions.map((k) => (
        <span key={k.name} className="chip condition">
          {conditionLabel(k)}{' '}
          <button type="button" className="link-button" aria-label={`Remove ${k.name} from ${who}`} title="Remove" onClick={() => onRemove(k)}>
            ✕
          </button>
        </span>
      ))}
      <form
        className="condition-add"
        aria-label={`Add a condition to ${who}`}
        onSubmit={(e) => {
          e.preventDefault();
          if (name === '__other') {
            // "Hasted:3" reads as three rounds, as on the desktop.
            const typed = decodeConditions(other)[0];
            if (typed) onAdd(typed.name, typed.roundsLeft);
            setOther('');
          } else if (name) onAdd(name, rounds);
          setName('');
        }}
      >
        <select aria-label="Condition" value={name} onChange={(e) => setName(e.currentTarget.value)}>
          <option value="">+ Condition</option>
          {presets.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
          <option value="__other">Other…</option>
        </select>
        {name === '__other' && <input aria-label="Other condition" value={other} maxLength={100} placeholder="e.g. Blessed, or Hasted:3" onChange={(e) => setOther(e.currentTarget.value)} />}
        {durations && name !== '' && name !== '__other' && (
          <select aria-label="For" value={rounds} onChange={(e) => setRounds(Number(e.currentTarget.value))}>
            {CONDITION_DURATIONS.map((d) => (
              <option key={d} value={d}>
                {d === 0 ? 'Until removed' : `${d} ${d === 1 ? 'round' : 'rounds'}`}
              </option>
            ))}
          </select>
        )}
        {name !== '' && (
          <button type="submit" className="quiet">
            Add
          </button>
        )}
      </form>
    </div>
  );
}

// ---- the encounter (EncounterRunner) ----

function EncounterPane({ table }: { table: Table }) {
  const { run, view, file, campaignId, act } = table;
  const [panel, setPanel] = useState<'none' | 'new' | 'edit' | 'add'>('none');
  const active = run.active;
  const choose = (id: string) => void act(() => realms.setActiveEncounter(file, campaignId, id === '' ? null : id), 'The encounter could not be chosen.');
  const step = (command: RunCommand, fallback = 'That could not be done.') => act(() => realms.runEncounter(file, campaignId, active!.id, command, stamp()), fallback);
  return (
    <>
      <div className="encounter-picker">
        <label>
          <span className="visually-hidden">Encounter</span>
          <select value={active?.id ?? ''} onChange={(e) => choose(e.currentTarget.value)} title="The encounter to run at the table">
            <option value="">Pick an encounter…</option>
            {view.encounters.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
                {e.round > 0 ? ` (round ${e.round})` : ''}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="quiet" title="New encounter" onClick={() => setPanel(panel === 'new' ? 'none' : 'new')}>
          + New
        </button>
      </div>
      {panel === 'new' && (
        <EncounterForm
          heading="New encounter"
          submit="Make encounter"
          sessions={view.sessions}
          onCancel={() => setPanel('none')}
          onSave={(details) =>
            act(async () => {
              const { id } = await realms.createEncounter(file, campaignId, details);
              await realms.setActiveEncounter(file, campaignId, id);
            }, 'The encounter could not be made.').then((ok) => ok && setPanel('none'))
          }
        />
      )}
      {!active ? (
        <p className="meta">Pick an encounter to run, or + New to make one. The party stays on the left and the dock on the right either way.</p>
      ) : (
        <>
          <h2>{active.name}</h2>
          <div className="actions turn-controls">
            <span className="round">{active.round > 0 ? `Round ${active.round}` : 'Not started'}</span>
            {active.round <= 0 ? (
              <button
                type="button"
                className="primary"
                disabled={active.combatants.length === 0}
                title="Round 1, highest initiative first. Combatants who share a character sheet take their hit points and conditions from it."
                onClick={() => void step({ kind: 'start' })}
              >
                ▶ Start encounter
              </button>
            ) : (
              <>
                <button type="button" title="Back to the previous turn" onClick={() => void step({ kind: 'previous' })}>
                  ◀ Back
                </button>
                <button type="button" className="primary" title="End this turn (timed conditions count down) and move to the next" onClick={() => void step({ kind: 'next' })}>
                  Next turn ▶
                </button>
                <button type="button" className="quiet" title="End the encounter" onClick={() => void step({ kind: 'end' })}>
                  ■ End
                </button>
              </>
            )}
          </div>
          {active.description && <p className="pre-line meta">{active.description}</p>}
          <div className="actions">
            <button type="button" className="quiet" onClick={() => setPanel(panel === 'add' ? 'none' : 'add')}>
              + Add combatant
            </button>
            <button type="button" className="quiet" title="Add one to three of the realm's Creature entries, with initiative and hit points from their sheets when they have one" onClick={() => void step({ kind: 'quickAdd' })}>
              🎲 Quick Add Creatures
            </button>
            <button type="button" className="quiet" title="Everyone rolls again: from their character sheet when linked to one, otherwise a d20" onClick={() => void step({ kind: 'rollInitiative' })}>
              🎲 Roll initiative
            </button>
            <button type="button" className="quiet" title="Name, description, difficulty, and session" onClick={() => setPanel(panel === 'edit' ? 'none' : 'edit')}>
              Edit
            </button>
          </div>
          {panel === 'edit' && (
            <EncounterForm
              heading={`Edit ${active.name}`}
              submit="Save"
              initial={{ ...active, combatantCount: active.combatants.length }}
              sessions={view.sessions}
              onCancel={() => setPanel('none')}
              onSave={(details) => act(() => realms.updateEncounterDetails(file, active.id, details), 'The encounter could not be saved.').then((ok) => ok && setPanel('none'))}
            />
          )}
          {panel === 'add' && <CombatantForm heading="New combatant" submit="Add" onCancel={() => setPanel('none')} onSave={(details) => step({ kind: 'addCombatant', details }).then((ok) => ok && setPanel('none'))} />}
          {active.combatants.length === 0 && <p className="meta">No combatants yet. Add them one at a time, or Quick Add a few of the realm's creatures.</p>}
          <ol className="combatants">
            {turnOrder(active.combatants).map((c) => (
              <CombatantRow key={`${c.id}-${c.initiative}`} combatant={c} table={table} current={active.round > 0 && active.currentCombatantId === c.id} step={step} />
            ))}
          </ol>
        </>
      )}
    </>
  );
}

function CombatantRow({ combatant: c, table, current, step }: { combatant: Combatant; table: Table; current: boolean; step: (command: RunCommand, fallback?: string) => Promise<boolean> }) {
  const { run, realmKey } = table;
  const [initiative, setInitiative] = useState(String(c.initiative));
  const [amount, setAmount] = useState('');
  const [editing, setEditing] = useState(false);
  const sheet = run.combatantSheets[c.id];
  const fraction = c.maxHp > 0 ? Math.max(0, c.currentHp / c.maxHp) : 0;
  const commitInitiative = () => {
    const value = Number(initiative.trim());
    if (!Number.isInteger(value)) setInitiative(String(c.initiative));
    else if (value !== c.initiative) void step({ kind: 'setInitiative', combatantId: c.id, initiative: value });
  };
  const hit = (kind: 'damage' | 'heal') => {
    if (amount.trim() === '') return;
    // Cleared once done, unless the next amount has been typed meanwhile.
    void step({ kind, combatantId: c.id, amount }).then((ok) => ok && setAmount((now) => (now === amount ? '' : now)));
  };
  return (
    <li className={['combatant', current ? 'current-turn' : '', isDown(c) ? 'down' : ''].filter(Boolean).join(' ')} aria-label={c.name} aria-current={current ? 'true' : undefined} title={c.notes || undefined}>
      <div className="combatant-top">
        <span className="turn-marker" aria-hidden="true">
          {current ? '▶' : ''}
        </span>
        <label>
          <span className="visually-hidden">Initiative</span>
          <input className="initiative" inputMode="numeric" value={initiative} title="Initiative: type a new value and press Enter" onChange={(e) => setInitiative(e.currentTarget.value)} onBlur={commitInitiative} onKeyDown={(e) => e.key === 'Enter' && commitInitiative()} />
        </label>
        <span className="combatant-name">
          <strong>{c.name}</strong>
          {c.playerCharacter && <span className="chip">PC</span>}
          {sheet && (
            <Link
              to="/realm/$realmKey/sheets/$sheetId"
              params={{ realmKey, sheetId: sheet.sheetId }}
              title={`${sheet.name}'s sheet: ${sheet.summary}\n${sheet.shares ? 'Hit points and conditions are kept in step with the sheet.' : 'Several combatants use this sheet, so each keeps its own hit points here.'}`}
            >
              {sheet.shares ? '📜 ⇄' : '📜'}
            </Link>
          )}
        </span>
        <span className="hp">
          <progress max={1} value={fraction} className={fraction <= 0.25 ? 'low' : undefined} aria-label={`${c.currentHp} of ${c.maxHp} hit points`} />
          <span className="meta">
            {c.currentHp} / {c.maxHp} HP
          </span>
        </span>
        <span className="combatant-tools">
          <button type="button" className="quiet" title="Change this combatant's name, entry, initiative, and hit points" onClick={() => setEditing(!editing)}>
            Edit
          </button>
          <button type="button" className="quiet" aria-label={`Remove ${c.name}`} title="Remove from the encounter" onClick={() => void step({ kind: 'removeCombatant', combatantId: c.id })}>
            ✕
          </button>
        </span>
      </div>
      <div className="combatant-bottom">
        <form
          className="damage"
          aria-label={`Damage or heal ${c.name}`}
          onSubmit={(e) => {
            e.preventDefault();
            hit('damage');
          }}
        >
          <input aria-label="Amount" value={amount} placeholder="7 or 2d6+3" maxLength={100} title="Damage or healing: a number, or a roll like 2d6+3. Enter hits." onChange={(e) => setAmount(e.currentTarget.value)} />
          <button type="submit" title="Take this much damage (a roll is rolled first)">
            Hit
          </button>
          <button type="button" title="Heal this much (a roll is rolled first)" onClick={() => hit('heal')}>
            Heal
          </button>
        </form>
        <Conditions
          conditions={c.conditions}
          presets={run.conditionPresets[c.id] ?? []}
          durations
          who={c.name}
          onRemove={(k) => void step({ kind: 'removeCondition', combatantId: c.id, name: k.name })}
          onAdd={(name, rounds) => void step({ kind: 'addCondition', combatantId: c.id, name, rounds })}
        />
      </div>
      {editing && (
        <CombatantForm
          heading={`Edit ${c.name}`}
          submit="Save"
          initial={c}
          onCancel={() => setEditing(false)}
          onSave={(details) => step({ kind: 'updateCombatant', combatantId: c.id, details }).then((ok) => ok && setEditing(false))}
        />
      )}
    </li>
  );
}

function CombatantForm({ heading, submit, initial, onSave, onCancel }: { heading: string; submit: string; initial?: Combatant; onSave: (details: CombatantDetails) => Promise<unknown>; onCancel: () => void }) {
  const { entries } = realmRoute.useLoaderData();
  const [name, setName] = useState(initial?.name ?? '');
  const [entryId, setEntryId] = useState(initial?.linkedEntryId ?? '');
  const [initiative, setInitiative] = useState(String(initial?.initiative ?? 0));
  const [maxHp, setMaxHp] = useState(String(initial?.maxHp ?? 10));
  const [currentHp, setCurrentHp] = useState(String(initial?.currentHp ?? 10));
  const [pc, setPc] = useState(initial?.playerCharacter ?? false);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const whole = (text: string) => (Number.isInteger(Number(text)) ? Number(text) : 0);
  return (
    <form
      className="panel"
      aria-label={heading}
      onSubmit={(e) => {
        e.preventDefault();
        const chosen = entries.find((x) => x.id === entryId);
        void onSave({ name: name.trim() === '' && chosen ? chosen.title : name, linkedEntryId: entryId === '' ? null : entryId, initiative: whole(initiative), maxHp: whole(maxHp), currentHp: whole(currentHp), playerCharacter: pc, notes });
      }}
    >
      <h3>{heading}</h3>
      <label>
        Name
        <input value={name} maxLength={200} autoFocus placeholder="e.g. Goblin Archer" onChange={(e) => setName(e.currentTarget.value)} />
      </label>
      <label>
        Linked entry
        <select value={entryId} onChange={(e) => setEntryId(e.currentTarget.value)}>
          <option value="">(none)</option>
          {entries.map((x: EntrySummary) => (
            <option key={x.id} value={x.id}>
              {x.title} ({x.typeName})
            </option>
          ))}
        </select>
      </label>
      <div className="date-row">
        <label>
          Initiative
          <input type="number" min={-20} max={40} value={initiative} onChange={(e) => setInitiative(e.currentTarget.value)} />
        </label>
        <label>
          Max HP
          <input type="number" min={0} max={9999} value={maxHp} onChange={(e) => setMaxHp(e.currentTarget.value)} />
        </label>
        <label>
          Current HP
          <input type="number" min={-9999} max={9999} value={currentHp} onChange={(e) => setCurrentHp(e.currentTarget.value)} />
        </label>
      </div>
      <label className="check">
        <input type="checkbox" checked={pc} onChange={(e) => setPc(e.currentTarget.checked)} />
        Player character
      </label>
      <label>
        Notes
        <textarea value={notes} rows={2} maxLength={4000} placeholder="Optional" onChange={(e) => setNotes(e.currentTarget.value)} />
      </label>
      {!initial && <p className="meta">Left at initiative 0 and 10 / 10 hit points, a linked entry's character sheet fills them in.</p>}
      <div className="actions">
        <button type="submit" className="primary">
          {submit}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---- the dock (GmDock) ----

function Dock({ table, register }: { table: Table; register: (flush: () => Promise<void>) => void }) {
  const { view, file, campaignId, act } = table;
  const current = view.sessions.find((s) => s.id === view.campaign.currentSessionId) ?? null;
  const [starting, setStarting] = useState(false);
  const [title, setTitle] = useState('');
  return (
    <>
      <details open className="dock-section">
        <summary>📝 Session</summary>
        <div className="encounter-picker">
          <label>
            <span className="visually-hidden">Session</span>
            <select value={current?.id ?? ''} title="The session being played: its notes and play log are shown here" onChange={(e) => void act(() => realms.setCurrentSession(file, campaignId, e.currentTarget.value || null), 'The session could not be chosen.')}>
              <option value="">Which session is this?</option>
              {view.sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  Session {s.sessionNumber}: {s.title}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="quiet" title="Start a new session and make it the current one" onClick={() => {
              setTitle(`Session ${view.sessions.length + 1}`);
              setStarting(!starting);
            }}>
            + New
          </button>
        </div>
        {starting && (
          <form
            className="panel"
            aria-label="New session"
            onSubmit={(e) => {
              e.preventDefault();
              void act(async () => {
                const { id } = await realms.createSession(file, campaignId);
                if (title.trim() !== '') await realms.updateSession(file, id, { title });
                await realms.setCurrentSession(file, campaignId, id);
              }, 'The session could not be started.').then((ok) => ok && setStarting(false));
            }}
          >
            <label>
              What's this session called?
              <input value={title} required maxLength={CAMPAIGN_LIMITS.name} autoFocus onChange={(e) => setTitle(e.currentTarget.value)} />
            </label>
            <div className="actions">
              <button type="submit" className="primary">
                Start session
              </button>
              <button type="button" onClick={() => setStarting(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}
        {current ? (
          <SessionNotes key={current.id} session={current} table={table} register={register} />
        ) : (
          <p className="meta">Pick or start a session to keep notes and a play log: fights, rounds, and table rolls are written to it as they happen.</p>
        )}
      </details>
      <details open className="dock-section">
        <summary>🎲 Dice</summary>
        <DiceTray table={table} />
      </details>
      <details open className="dock-section">
        <summary>📜 Random tables</summary>
        <TableRoller table={table} />
      </details>
      <details open className="dock-section">
        <summary>🗺 Player View</summary>
        <PlayerViewSection table={table} />
      </details>
    </>
  );
}

/**
 * The dock's Player View (`GmDock.playerViewSection`): which map players see, Show to players
 * or Close, and the map's named regions to reveal or cover again - or Turn Fog On.
 */
function PlayerViewSection({ table }: { table: Table }) {
  const { file, act, log } = table;
  const playerView = usePlayerView();
  const [maps, setMaps] = useState<readonly { id: string; name: string }[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [map, setMap] = useState<AtlasMapView | null>(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    void realms.maps(file).then((all) => setMaps(all.map((m) => ({ id: m.id, name: m.name }))));
  }, [file]);
  const mapId = chosen ?? playerView.mapId ?? maps?.[0]?.id ?? null;
  useEffect(() => {
    if (!mapId) return;
    let live = true;
    void realms.atlasMap(file, mapId).then((view) => live && setMap(view));
    return () => {
      live = false;
    };
  }, [file, mapId, version]);
  if (!maps) return <p className="meta">Loading…</p>;
  if (maps.length === 0) return <p className="meta">This realm has no maps yet.</p>;
  const showing = playerView.mapId === mapId;
  const name = maps.find((m) => m.id === mapId)?.name ?? '';
  /** A fog change, then its line in the play log, as the desktop writes it. */
  async function change(work: () => Promise<unknown>, line: string) {
    if (!(await act(work, 'The fog could not be changed.'))) return;
    await log(line);
    setVersion((n) => n + 1);
  }
  const revealed = map ? parseFog(map.map.fog.revealed) : [];
  const regions = map ? map.regions.filter((r) => r.label.trim() !== '' && r.points.length >= REGION_MIN_POINTS) : [];
  return (
    <div className="player-view-section">
      <div className="encounter-picker">
        <label>
          <span className="visually-hidden">The map players see</span>
          <select value={mapId ?? ''} title="The map the players see" onChange={(e) => setChosen(e.currentTarget.value)}>
            {maps.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        {showing ? (
          <button type="button" onClick={playerView.close}>
            Close Player View
          </button>
        ) : (
          <button
            type="button"
            className="primary"
            title="The Player View is a second window for a TV or second screen, showing only what players can see"
            onClick={() => {
              if (!mapId) return;
              playerView.show(mapId);
              void log(`Showed the players ${name}.`);
            }}
          >
            Show to players
          </button>
        )}
      </div>
      {map && !map.map.fog.enabled && (
        <>
          <p className="meta">Fog of war is off for this map - players see all of it.</p>
          <button type="button" title="Hide the map from the players until you reveal it" onClick={() => void change(() => realms.updateFog(file, map.map.id, true, map.map.fog.revealed), `Turned fog of war on for ${name}.`)}>
            Turn fog on
          </button>
        </>
      )}
      {map && map.map.fog.enabled && regions.length === 0 && <p className="meta">This map has no named regions to reveal - use the Atlas's Fog tool.</p>}
      {map && map.map.fog.enabled && regions.length > 0 && (
        <ul className="player-view-regions" aria-label="Regions to reveal">
          {regions.map((r) => {
            const shown = revealsAny(true, revealed, r.points);
            return (
              <li key={r.id}>
                <span>{r.label}</span>
                <span className="meta">{shown ? 'revealed' : 'hidden'}</span>
                <button
                  type="button"
                  title={shown ? 'Hide this region from the players again' : 'Show this region to the players'}
                  onClick={() =>
                    void change(
                      () => realms.updateFog(file, map.map.id, true, encodeFog(shown ? hideFog(revealed, polygonShape(r.points)) : revealFog(revealed, polygonShape(r.points)))),
                      shown ? `Covered ${r.label} on ${name} again.` : `Revealed ${r.label} on ${name} to the players.`,
                    )
                  }
                >
                  {shown ? 'Cover' : 'Reveal'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Each `[[...]]` in the text, once. */
function inlineRolls(text: string): string[] {
  return [...new Set([...text.matchAll(/\[\[([^\]]+)\]\]/g)].map((m) => m[1]!.trim()))].filter((e) => e !== '').slice(0, 20);
}

function SessionNotes({ session, table, register }: { session: CampaignSession; table: Table; register: (flush: () => Promise<void>) => void }) {
  const { file, log } = table;
  const { change, flush, status } = useAutosaver<SessionChanges>((changes) => realms.updateSession(file, session.id, changes));
  register(flush);
  const [notes, setNotes] = useState(session.notes);
  const [playLog, setPlayLog] = useState(session.playLog);
  // The log grows from elsewhere (a turn, a roll); every action saves this editor first, so
  // the stored log already holds its edits. The notes are only written here, so they stay as typed.
  const seenLog = useRef(session.playLog);
  const logRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (session.playLog === seenLog.current) return;
    seenLog.current = session.playLog;
    setPlayLog(session.playLog);
    // Newest lines are at the bottom.
    requestAnimationFrame(() => {
      if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
    });
  }, [session.playLog]);
  const [scanning, setScanning] = useState(false);
  const [rollError, setRollError] = useState('');
  const rolls = inlineRolls(notes);
  return (
    <div className="session-notes">
      <SaveStatusLine status={status} />
      <div className="notes-head">
        <span>Notes</span>
        <button type="button" className="quiet" title="Find the lore this session's notes and play log mention, and add it to the campaign's cast" onClick={() => void flush().then(() => setScanning(!scanning))}>
          🔗 Scan notes
        </button>
      </div>
      <label>
        <span className="visually-hidden">Session notes</span>
        <textarea
          value={notes}
          rows={4}
          maxLength={CAMPAIGN_LIMITS.text}
          placeholder="Notes for this session. Write [[2d6]] to roll it from here."
          onChange={(e) => {
            setNotes(e.currentTarget.value);
            change({ notes: e.currentTarget.value });
          }}
        />
      </label>
      {rolls.length > 0 && (
        <p className="inline-rolls">
          {rolls.map((expression) => (
            <button
              key={expression}
              type="button"
              className="quiet"
              title="Roll it: the result goes into the play log and the Dice Roller's history"
              onClick={() => {
                setRollError('');
                try {
                  const result = DiceExpression.parse(expression).roll(secureRandom);
                  recordRoll(`${session.title}: notes`, result);
                  void log(`Rolled [[${expression}]]: ${describeRoll(result)}`);
                } catch (e) {
                  setRollError(message(e, 'That roll could not be read.'));
                }
              }}
            >
              🎲 {expression}
            </button>
          ))}
        </p>
      )}
      {rollError && (
        <p className="notice-error" role="alert">
          {rollError}
        </p>
      )}
      {scanning && <NotesScan table={table} session={session} onClose={() => setScanning(false)} />}
      <label>
        Play log: written as things happen; edit freely
        <textarea
          className="play-log"
          value={playLog}
          rows={9}
          maxLength={CAMPAIGN_LIMITS.playLog}
          placeholder="Nothing logged yet."
          ref={(el) => {
            logRef.current = el;
            // Newest lines are at the bottom.
            if (el && el.dataset.scrolled !== 'yes') {
              el.scrollTop = el.scrollHeight;
              el.dataset.scrolled = 'yes';
            }
          }}
          onChange={(e) => {
            setPlayLog(e.currentTarget.value);
            change({ playLog: e.currentTarget.value });
          }}
        />
      </label>
    </div>
  );
}

function DiceTray({ table }: { table: Table }) {
  const { history } = useDice();
  const [expression, setExpression] = useState('');
  const [error, setError] = useState('');
  return (
    <>
      <form
        className="encounter-picker"
        aria-label="Roll dice"
        onSubmit={(e) => {
          e.preventDefault();
          setError('');
          try {
            const result = DiceExpression.parse(expression).roll(secureRandom);
            recordRoll('', result);
            void table.log(`Rolled ${result.expression}: ${describeRoll(result)}`);
          } catch (err) {
            setError(message(err, 'That roll could not be read.'));
          }
        }}
      >
        <input aria-label="Roll" value={expression} placeholder="1d20 + 5" maxLength={500} onChange={(e) => setExpression(e.currentTarget.value)} />
        <button type="submit" className="primary" title="Roll it (Enter): it goes into the play log and the Dice Roller's history">
          Roll
        </button>
      </form>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      <ul className="recent-rolls">
        {history.slice(0, 3).map((r) => (
          <li key={r.at + r.text}>
            {r.label && <span className="meta">{r.label}: </span>}
            {r.text}
          </li>
        ))}
      </ul>
    </>
  );
}

function TableRoller({ table }: { table: Table }) {
  const { run, file } = table;
  const [tableId, setTableId] = useState(run.tables[0]?.id ?? '');
  const [result, setResult] = useState<{ text: string; note: string } | null>(null);
  const [error, setError] = useState('');
  if (run.tables.length === 0) return <p className="meta">This realm has no random tables yet.</p>;
  return (
    <>
      <form
        className="encounter-picker"
        aria-label="Roll on a table"
        onSubmit={(e) => {
          e.preventDefault();
          setError('');
          void (async () => {
            try {
              const found = await realms.getTable(file, tableId);
              const rolled = found ? rollTable(found.entries, secureRandom) : null;
              if (!found || !rolled) return;
              // Results can carry their own dice: "[[1d4+1]] Drax War Hounds" rolls the number.
              const expanded = expandInlineRolls(rolled.entry.text, secureRandom);
              for (const r of expanded.rolls) recordRoll(found.name, r);
              const inline = expanded.rolls.length === 0 ? '' : ` · ${expanded.rolls.map(describeRoll).join(', ')}`;
              setResult({ text: expanded.text, note: `Rolled ${rolled.roll} on a d${rolled.die}${inline}` });
              await table.log(`${found.name}: ${expanded.text} (${rolled.roll} on a d${rolled.die})`);
            } catch (err) {
              setError(message(err, 'The table could not be rolled.'));
            }
          })();
        }}
      >
        <select aria-label="Random table" value={tableId} onChange={(e) => setTableId(e.currentTarget.value)}>
          {run.tables.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <button type="submit" className="primary" title="Roll on the table: the result goes into the play log">
          Roll
        </button>
      </form>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {result && (
        <>
          <p className="table-result" aria-live="polite">
            {result.text}
          </p>
          <p className="meta">{result.note}</p>
        </>
      )}
    </>
  );
}

// ---- Scan Notes (NotesScanDialog) ----

interface Group {
  readonly key: string;
  readonly candidates: readonly Candidate[];
  readonly mentions: { start: number; end: number; text: string }[];
}

function NotesScan({ table, session, onClose }: { table: Table; session: CampaignSession; onClose: () => void }) {
  const { file, view, campaignId, realmKey, act } = table;
  const router = useRouter();
  const { entries } = realmRoute.useLoaderData();
  const scope = `campaign:${campaignId}`;
  const [ignored, setIgnored] = useState<readonly IgnoredMention[] | null>(null);
  const [chosen, setChosen] = useState<ReadonlyMap<string, string>>(new Map());
  const [types, setTypes] = useState<ReadonlyMap<string, string>>(new Map());
  const reload = () => realms.ignoredMentions(file, scope).then(setIgnored, () => setIgnored([]));
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, scope]);

  // What the GM wrote, and the play log: searched for entries; only what was written offers new names.
  const written = [session.summary, session.notes].join('\n\n').trim();
  const text = `${written}\n\n${session.playLog}`.trim();
  const index = useMemo(() => NameIndex.of(entries), [entries]);
  const keys = new Set((ignored ?? []).map((i) => i.key));
  const groups = useMemo(() => {
    const byKey = new Map<string, Group>();
    for (const m of scanMentions(text, index)) {
      if (keys.has(ignoreKeyForCandidates(m.candidates))) continue;
      const key = m.candidates.map((c) => c.entryId).join(',');
      const group = byKey.get(key) ?? { key, candidates: m.candidates, mentions: [] };
      group.mentions.push(m);
      byKey.set(key, group);
    }
    return [...byKey.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, index, ignored]);
  const unknown = unknownNames(written, index, scanMentions(written, index)).filter((n) => !keys.has(ignoreKeyForName(n.name)));
  const { campaign } = view;
  const label = (g: Group) => [...new Set(g.candidates.map((c) => c.title))].join(' / ');
  const ignore = (key: string, name: string) => void act(() => realms.ignoreMention(file, scope, key, name).then(reload), 'That could not be ignored.');

  return (
    <section className="panel scene-scan" aria-label="Scan notes">
      <h3>Scan notes for connections</h3>
      <p className="meta" role="status">
        {groups.length === 0 && unknown.length === 0
          ? `“${session.title}”: no new connections found.`
          : `“${session.title}” mentions ${groups.length} ${groups.length === 1 ? 'entry' : 'entries'}${unknown.length === 0 ? '' : `, and ${unknown.length} ${unknown.length === 1 ? 'name' : 'names'} with no entry yet`}. Add the ones who matter to the campaign's cast.`}
      </p>
      {groups.length > 0 && <h4>Mentioned in this session</h4>}
      {groups.map((g) => {
        const entryId = g.candidates.length === 1 ? g.candidates[0]!.entryId : (chosen.get(g.key) ?? null);
        const inParty = entryId !== null && campaign.playerCharacterIds.includes(entryId);
        const inCast = entryId !== null && campaign.castIds.includes(entryId);
        const found = [...new Set(g.mentions.map((m) => `“${m.text}”`))].join(', ') + (g.mentions.length > 1 ? ` ×${g.mentions.length}` : '');
        return (
          <div key={g.key} className="scan-card" role="group" aria-label={label(g)}>
            <div className="scan-head">
              {g.candidates.length === 1 ? (
                <>
                  <strong>{g.candidates[0]!.title}</strong> <span className="chip">{ENTITY_TYPE_NAMES[g.candidates[0]!.entityType] ?? g.candidates[0]!.entityType}</span>
                </>
              ) : (
                <select aria-label="Which one does this mean?" value={entryId ?? ''} onChange={(e) => setChosen(new Map(chosen).set(g.key, e.currentTarget.value))}>
                  <option value="">Which one does this mean?</option>
                  {g.candidates.map((c) => (
                    <option key={c.entryId} value={c.entryId}>
                      {c.title}
                    </option>
                  ))}
                </select>
              )}
              <span className="meta">found as {found}</span>
              <button
                type="button"
                className="primary"
                disabled={entryId === null || inParty || inCast}
                onClick={() => entryId && void act(() => realms.updateCampaign(file, campaignId, { castIds: [...campaign.castIds, entryId] }), 'The cast could not be changed.')}
              >
                {inParty ? '✓ In the party' : inCast ? '✓ In the cast' : 'Add to cast'}
              </button>
              <button type="button" className="quiet" disabled={entryId === null} onClick={() => entryId && void router.navigate({ to: '/realm/$realmKey/entry/$entryId', params: { realmKey, entryId }, search: {} })}>
                Open
              </button>
              <button type="button" className="quiet" onClick={() => ignore(ignoreKeyForCandidates(g.candidates), label(g))}>
                Ignore
              </button>
            </div>
            {g.mentions.slice(0, 3).map((m) => (
              <p key={m.start} className="meta">
                {before(text, m.start)}
                <mark>{m.text}</mark>
                {after(text, m.end)}
              </p>
            ))}
          </div>
        );
      })}
      {unknown.length > 0 && <h4>Names with no entry yet</h4>}
      {unknown.map((n) => (
        <div key={n.name} className="scan-card" role="group" aria-label={n.name}>
          <div className="scan-head">
            <strong>
              {n.name}
              {n.count > 1 ? ` ×${n.count}` : ''}
            </strong>
            <select aria-label={`Type for ${n.name}`} value={types.get(n.name) ?? n.type} onChange={(e) => setTypes(new Map(types).set(n.name, e.currentTarget.value))}>
              {Object.keys(ENTITY_TYPE_NAMES)
                .filter((t) => t !== 'CUSTOM')
                .map((t) => (
                  <option key={t} value={t}>
                    {ENTITY_TYPE_NAMES[t]}
                  </option>
                ))}
            </select>
            <button
              type="button"
              onClick={() =>
                void act(() => realms.createEntry(file, { title: n.name, entityType: types.get(n.name) ?? n.type, audience: { role: settings()['new.entryAudience'], customLabel: '' } }), 'The entry could not be made.')
              }
            >
              Create entry
            </button>
            <button type="button" className="quiet" title="Don't suggest this name here again" onClick={() => ignore(ignoreKeyForName(n.name), n.name)}>
              Ignore
            </button>
          </div>
          <p className="meta">
            {n.before}
            <mark>{n.name}</mark>
            {n.after}
          </p>
        </div>
      ))}
      {(ignored ?? []).length > 0 && (
        <>
          <h4>Ignored here</h4>
          <p className="chips">
            {ignored!.map((i) => (
              <span key={i.key} className="chip">
                {i.label.trim() === '' ? '(unnamed)' : i.label}{' '}
                <button type="button" className="link-button" onClick={() => void act(() => realms.stopIgnoringMention(file, scope, i.key).then(reload), 'That could not be put back.')}>
                  Stop ignoring
                </button>
              </span>
            ))}
          </p>
        </>
      )}
      <div className="actions">
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>
    </section>
  );
}
